import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { useSnackbar } from "notistack";
import { Box, Chip, Container, Typography, CircularProgress, Grid, Card, CardContent, Avatar, Tooltip, Switch, IconButton} from "@mui/material";
import { Work, Assignment, Flag, CheckCircle, Publish, Info, Visibility, Email } from "@mui/icons-material";
import sh from "bundles/common/utils/sh";

const Reports = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentUser = useSelector((state) => state.current_user);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [timeFilter, setTimeFilter] = useState("global");
  const [regionalDepartments, setRegionalDepartments] = useState([]);
  const [selectedRegionalDepartments, setSelectedRegionalDepartments] = useState([]);

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f5f5f5",
        fontWeight: "bold",
        fontSize: "14px"
      }
    },
    cells: {
      style: {
        fontSize: "13px"
      }
    },
    table: {
      style: {
        width: "100%"
      }
    },
    tableWrapper: {
      style: {
        width: "100%",
        marginBottom: "24px"
      }
    }
  };

  const columns = [
    {
      name: "User",
      selector: row => row.name,
      cell: row => (
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar
            src={row.avatar_url}
            sx={{ width: 32, height: 32, bgcolor: "primary.main" }}
          >
            {row.name.split(" ").map(n => n[0]).join("").toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {row.name}
            </Typography>

          </Box>
        </Box>
      ),
      sortable: true
    },
    {
      name: "Published Jobs",
      selector: row => row.published_job_offers,
      cell: row => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Publish fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight="medium">
            {row.published_job_offers}
          </Typography>
        </Box>
      ),
      sortable: true,
      center: true
    },
    {
      name: "Online Jobs",
      selector: row => row.online_job_offers,
      cell: row => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <CheckCircle fontSize="small" color="success" />
          <Typography variant="body2" fontWeight="medium">
            {row.online_job_offers}
          </Typography>
        </Box>
      ),
      sortable: true,
      center: true
    },
    {
      name: "On Stepstone",
      selector: row => row.stepstone_jobs_count,
      cell: row => (
        <Typography variant="body1" fontWeight="medium">
          <strong>{row.stepstone_jobs_count}</strong>
        </Typography>
      ),
      sortable: true,
      center: true
    },
    {
      name: "On Indeed",
      selector: row => row.indeed_jobs_count,
      cell: row => (
        <Typography variant="body1" fontWeight="medium">
          <strong>{row.indeed_jobs_count}</strong>
        </Typography>
      ),
      sortable: true,
      center: true
    },
    {
      name: "Job Applications",
      selector: row => row.applications_count,
      cell: row => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Assignment fontSize="small" color="info" />
          <Typography variant="body2" fontWeight="medium">
            {row.applications_count}
          </Typography>
        </Box>
      ),
      sortable: true,
      center: true
    },
    {
      name: "Flag Status",
      selector: row => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="Good flags">
            <Chip
              label={row.flag_statistics.good}
              size="small"
              color="success"
            />
          </Tooltip>
          <Tooltip title="Maybe flags">
            <Chip
              label={row.flag_statistics.maybe}
              size="small"
              color="warning"
            />
          </Tooltip>
          <Tooltip title="No go flags">
            <Chip
              label={row.flag_statistics.no_go}
              size="small"
              color="error"
            />
          </Tooltip>
        </Box>
      ),
      center: true
    },
    {
      name: "Unflagged",
      selector: row => row.flag_statistics.unflagged,
      cell: row => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Flag fontSize="small" color={row.flag_statistics.unflagged > 0 ? "warning" : "success"} />
          <Typography
            variant="body2"
            fontWeight="medium"
            color={row.flag_statistics.unflagged > 0 ? "warning.main" : "success.main"}
          >
            {row.flag_statistics.unflagged}
          </Typography>
        </Box>
      ),
      sortable: true,
      center: true
    },
    {
      name: "Actions",
      cell: row => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="View All Jobs">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleViewJobs(row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Indeed Jobs">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleViewIndeedJobs(row)}
            >
              <Info sx={{ color: "#003A9B" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={`Send email to ${row.first_name}`}>
            <IconButton
              size="small"
              onClick={() => handleMailUser(row)}
              sx={{ color: "#5A5A5A" }}
            >
              <Email />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      center: true
    }
  ];

  const fetchUsers = (filter = timeFilter) => {
    setLoading(true);
    const params = { time_filter: filter };
    if (selectedRegionalDepartments.length > 0) params.regional_department_ids = selectedRegionalDepartments;

    sh.get("internal/reports_data", { params })
      .then((res) => {
        setUsers(res?.data?.users || []);
      })
      .catch((err) => {
        console.error("Failed to fetch users data:", err);
        enqueueSnackbar("Failed to load users data", { variant: "error" });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchSummary = (filter = timeFilter) => {
    setSummaryLoading(true);
    sh.get("internal/reports_summary", { params: { time_filter: filter } })
      .then((res) => {
        setSummary(res?.data?.summary || {});
      })
      .catch((err) => {
        console.error("Failed to fetch summary data:", err);
        enqueueSnackbar("Failed to load summary data", { variant: "error" });
      })
      .finally(() => {
        setSummaryLoading(false);
      });
  };


  const fetchAllRegionalDepartments = () => {
    sh.get("internal/all_regional_departments").then((res) => {
      setRegionalDepartments(res?.data?.regional_departments || []);
    });
  };

  const handleRegionalDepartmentClick = (departmentId) => {
    setSelectedRegionalDepartments(prev => {
      const isSelected = prev.includes(departmentId);
      if (isSelected) {
        return prev.filter(id => id !== departmentId);
      } else {
        return [...prev, departmentId];
      }
    });
  };

  const handleViewJobs = (user) => {
    window.open(`/internal/jobs?user_id=${user.id}`, "_blank");
  };

  const handleViewIndeedJobs = (user) => {
    window.open(`/internal/jobs?user_id=${user.id}&on_indeed=true`, "_blank");
  };

  const handleMailUser = (user) => {
    window.location.href = `mailto:${user.email}`;
  };

  const redirectUnauthorizedUser = () => {
    navigate("/internal");
  };

  const handleTimeFilterChange = (event) => {
    const newFilter = event.target.checked ? "this_week" : "global";
    setTimeFilter(newFilter);
  };

  useEffect(() => {
    if (currentUser && currentUser["admin?"]) {
      fetchUsers();
    }
  }, [timeFilter, selectedRegionalDepartments]);

  useEffect(() => {
    if (currentUser && currentUser["admin?"]) {
      fetchSummary();
    }
  }, [timeFilter]);

  useEffect(() => {
    if (!currentUser || !currentUser["admin?"]) {
      redirectUnauthorizedUser();
    } else {
      fetchAllRegionalDepartments();
    }
  }, []);

  return (
    <Container maxWidth="100%">
      <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <Box style={{ marginTop: "26px" }}>
          <h1 style={{ fontSize: "24px" }}>Employee Activity Report</h1>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography
            variant="body2"
            color={timeFilter === "global" ? "primary.main" : "text.secondary"}
            sx={{ fontWeight: timeFilter === "global" ? 600 : 400 }}
          >
            Global
          </Typography>
          <Switch
            checked={timeFilter === "this_week"}
            onChange={handleTimeFilterChange}
            disabled={loading}
            color="primary"
          />
          <Typography
            variant="body2"
            color={timeFilter === "this_week" ? "primary.main" : "text.secondary"}
            sx={{ fontWeight: timeFilter === "this_week" ? 600 : 400 }}
          >
            This Week
          </Typography>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Published Job Offers
                  </Typography>
                  <Typography variant="h4">
                    {summaryLoading ? <CircularProgress size={24} /> : (summary.total_published_job_offers || 0)}
                  </Typography>
                </Box>
                <Work color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Online Jobs
                  </Typography>
                  <Typography variant="h4">
                    {summaryLoading ? <CircularProgress size={24} /> : (summary.total_online_jobs || 0)}
                  </Typography>
                </Box>
                <CheckCircle color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Job Applications
                  </Typography>
                  <Typography variant="h4">
                    {summaryLoading ? <CircularProgress size={24} /> : (summary.total_applications || 0)}
                  </Typography>
                </Box>
                <Assignment color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Stepstone Jobs (now)
                  </Typography>
                  <Typography variant="h4">
                    {summaryLoading ? <CircularProgress size={24} /> : (summary.total_stepstone_jobs || 0)}
                  </Typography>
                </Box>
                <img
                  src="/static/images/logos/StepStone.svg"
                  alt="Stepstone"
                  style={{ width: "100px" }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Indeed Jobs (now)
                  </Typography>
                  <Typography variant="h4">
                    {summaryLoading ? <CircularProgress size={24} /> : (summary.total_indeed_jobs || 0)}
                  </Typography>
                </Box>
                <img
                  src="/static/images/logos/Indeed.svg"
                  alt="Indeed"
                  style={{ width: "100px"}}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Regional Department Filter Buttons */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
        {regionalDepartments.map((department) => (
          <Chip
            key={department.id}
            label={department.name}
            onClick={() => handleRegionalDepartmentClick(department.id)}
            color={selectedRegionalDepartments.includes(department.id) ? "primary" : "default"}
            variant={selectedRegionalDepartments.includes(department.id) ? "filled" : "outlined"}
            sx={{ minWidth: 120, height: 40, fontSize: "14px" }}
          />
        ))}
      </Box>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={users}
        customStyles={customStyles}
        progressPending={loading}
        progressComponent={
          <Box sx={{ padding: "36px" }}>
            <CircularProgress />
          </Box>
        }
        noDataComponent={
          <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px" }}>
            <Typography variant="h6" color="textSecondary">
              No user data available
            </Typography>
          </Box>
        }
        striped
        highlightOnHover
      />
    </Container>
  );
};

export default Reports;
